import React, { useCallback, useEffect, useRef, useState } from "react";
import { startAudioStream, stopAudioStream, seizeControl, StatusFn } from "lib/audioStreamer";
import { useToast } from "providers/ToastProvider";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";

type UiState = "idle" | "connecting" | "live" | "contested";

interface SpeakerButtonProps {
    /** ws(s):// endpoint of the ARC's live_audio_sink service (see getLiveAudioWsUrl). */
    wsUrl: string | null;
    /** Display name shown to other clients when this user takes the speaker. */
    name?: string;
    /** Reports when this browser is actively sending microphone audio. */
    onActiveChange?: (active: boolean) => void;
}

/**
 * Bottom-bar push-to-talk speaker control.
 *  - idle:       breathing microphone glyph, invites a tap
 *  - connecting: dimmed microphone, connecting / awaiting grant
 *  - live:       animated waveform, audio flowing (you hold the speaker)
 *  - contested:  someone else holds the speaker → takeover prompt (the only time we confirm)
 *  - (transient) "X took the speaker" bubble on `revoked`
 *
 * Tapping a free speaker goes live immediately — no "take control" confirmation, since
 * there's no one to take it from. The confirm prompt appears only for a contested takeover.
 */
const SpeakerButton: React.FC<SpeakerButtonProps> = ({ wsUrl, name = "Someone", onActiveChange }) => {
    const [state, setState] = useState<UiState>("idle");
    const [bubble, setBubble] = useState(""); // revoked bubble text ('' = hidden)
    const bubbleTimer = useRef<ReturnType<typeof setTimeout>>();
    const { addToast } = useToast();
    const isSecure = useIsHttpSecure();

    useEffect(() => {
        onActiveChange?.(isSecure && state === "live");
        return () => onActiveChange?.(false);
    }, [isSecure, onActiveChange, state]);

    const onStatus: StatusFn = useCallback((s, info) => {
        if (s === "connecting" || s === "reconnecting") setState("connecting");
        else if (s === "live") setState("live");
        else if (s === "contested") setState("contested");
        else if (s === "revoked") {
            setState("idle");
            setBubble(`${info ?? "Someone"} took the speaker`);
            if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
            bubbleTimer.current = setTimeout(() => setBubble(""), 4000);
        }
    }, []);

    const stop = useCallback(async () => {
        await stopAudioStream();
        setState("idle");
    }, []);

    const start = async () => {
        setBubble("");
        if (!wsUrl) {
            addToast({ type: "warning", message: "Speaker unavailable (no endpoint)" });
            return;
        }
        try {
            await startAudioStream(wsUrl, { name, onStatus });
        } catch (e: any) {
            const raw = String(e?.message ?? e);
            const isGetUserMediaMissing =
                raw.includes("getUserMedia") || raw.includes("mediaDevices") || raw.includes("navigator.mediaDevices");

            const message = isGetUserMediaMissing
                ? "Microphone access is not available in this browser."
                : raw;

            addToast({ type: "warning", message });
            setState("idle");
        }
    };

    // idle → start (take a free speaker immediately, or land on a contested prompt);
    // any active state → stop (release / cancel connecting / decline takeover).
    const tap = () => {
        if (state !== "idle") {
            void stop();
            return;
        }
        void start();
    };

    const takeOver = () => {
        setState("connecting"); // seize sent; server replies granted → live (or revoked)
        seizeControl();
    };

    // Direct ARC production mode is served over plain HTTP by arc_gateway,
    // which is not a secure context — navigator.mediaDevices.getUserMedia does
    // not exist there. Fleet ARC connections carry isSecure=true from the
    // selected ARC provider, so this remains visible for fleet even when a
    // shared build .env contains REACT_APP_IS_ONLINE_MODE=false.
    // The getUserMedia error path in start stays as a backstop for browsers
    // that block the mic for other reasons.
    //
    // Placed after every hook so the hook order is unconditional.
    if (!isSecure) return null;

    const live = state === "live";
    const dimmed = state === "connecting" || state === "contested";

    return (
        <div className="relative inline-flex">
            {bubble && (
                <div
                    role="status"
                    className="absolute bottom-14 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-black100 px-2.5 py-1.5 text-h5l text-white shadow-lg after:absolute after:left-1/2 after:top-full after:-translate-x-1/2 after:border-4 after:border-transparent after:border-t-black100"
                >
                    {bubble}
                </div>
            )}

            <button
                type="button"
                aria-label={live ? "Release speaker" : "Take control of speaker"}
                onClick={tap}
                className={`grid h-11 w-11 place-items-center rounded-full ${
                    dimmed ? "text-black40" : live ? "text-primary" : "text-white"
                }`}
            >
                {live ? (
                    <svg className="h-6 w-6" viewBox="0 0 24 24" aria-hidden="true">
                        {[0, 1, 2, 3, 4].map((i) => (
                            <rect
                                key={i}
                                className="origin-center animate-dance"
                                style={{ animationDelay: `${i * 120}ms` }}
                                x={3 + i * 4}
                                y="8"
                                width="2.5"
                                height="8"
                                rx="1.25"
                                fill="currentColor"
                            />
                        ))}
                    </svg>
                ) : (
                    <svg
                        className={`h-6 w-6 ${
                            state === "idle" ? "origin-center animate-breathe motion-reduce:animate-none" : ""
                        }`}
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <rect x="9" y="3" width="6" height="12" rx="3" fill="currentColor" />
                        <path
                            d="M6 11v1a6 6 0 0012 0v-1M12 18v3m-3 0h6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </button>

            {state === "contested" && (
                <div
                    role="dialog"
                    aria-label="Take over the speaker"
                    className="absolute bottom-14 left-1/2 z-20 w-56 -translate-x-1/2 rounded-r10 border border-black70 bg-black90 p-3 shadow-xl"
                >
                    <p className="mb-2.5 text-h4m text-white">
                        Someone is on the speaker. Take over?
                    </p>
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="px-3 py-1.5 text-h4m text-black20"
                            onClick={() => void stop()}
                        >
                            Not now
                        </button>
                        <button
                            type="button"
                            className="rounded-r5 bg-primary px-3 py-1.5 text-h4m text-white"
                            onClick={takeOver}
                        >
                            Take over
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpeakerButton;
